import {
  Component,
  computed,
  EventEmitter,
  input,
  Output,
  signal,
  TemplateRef,
} from '@angular/core';
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  format,
  isEqual,
  startOfMonth,
  startOfToday,
  subMonths,
} from 'date-fns';
import * as moment from 'moment';

@Component({
  selector: 'app-calender',
  templateUrl: './calender.component.html',
  styleUrl: './calender.component.scss'
})
export class CalenderComponent {
  date : Date | null = null;
  @Output() monthChange: EventEmitter<Date> = new EventEmitter<Date>();
  @Output() filterByDateEvent = new EventEmitter<Date>();
  
  ngOnInit(){
    this.date = moment(this.currentMonth(), 'MMMM YYYY').toDate();
  }
  onChange($event: string){
    console.log($event);
    const date = moment($event, 'ddd MMM DD YYYY HH:mm:ss [GMT]Z');
    // const nextMonthDate = date.add(1, 'month');
    // console.log(nextMonthDate.toDate());
    const d = date.toDate();
    this.currentDate.set(d);
    this.updateDropdowns();
    this.monthChange.emit(this.currentDate());
    this.filterByDateEvent.emit(d)
  }
  public markers = input<CalendarMarkerData[], CalendarMarkerData[] | null>([], {
    transform: (value) => value ?? [],
  });

  public markerTpl = input<TemplateRef<{ $implicit: CalendarMarkerData[] }>>();

  protected currentDate = signal(startOfToday());
  protected currentMonth = computed(() => format(this.currentDate(), 'MMMM yyyy'));

  protected selectedMonth = this.currentDate().getMonth();
  protected selectedYear = this.currentDate().getFullYear();

  protected months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  protected years = Array.from({ length: 10 }, (_, i) => this.currentDate().getFullYear() - 5 + i);

  protected readonly startDateOfSelectedMonth = computed(() => startOfMonth(this.currentDate()));
  protected readonly endDateOfSelectedMonth = computed(() => endOfMonth(this.currentDate()));

  protected readonly days = computed(() => eachDayOfInterval({
    start: this.startDateOfSelectedMonth(),
    end: this.endDateOfSelectedMonth(),
  }));

  readonly #COL_START_CLASSES = ['', 'col-start-2', 'col-start-3', 'col-start-4', 'col-start-5', 'col-start-6', 'col-start-7'];

  readonly #markersMap = computed(() => {
    const map: Map<string, CalendarMarkerData[]> = new Map();
    this.markers().forEach((marker) => {
      const date = marker.date;
      const markers = map.get(this.getMarkerMapKey(date)) || [];
      markers.push(marker);
      map.set(this.getMarkerMapKey(date), markers);
    });
    return map;
  });

  protected readonly daysEnriched = computed(() => {
    return this.days().map((day, i) => {
      return {
        day: day,
        isToday: isEqual(day, startOfToday()),
        colStartClass: i === 0 ? this.#COL_START_CLASSES[day.getDay()] : '',
        markers: this.#markersMap().get(this.getMarkerMapKey(day)) || [],
      };
    });
  });

  readonly #dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  protected readonly dayNamesFormatted = this.#dayNames.map((dayName) => ({
    dayName: dayName,
    isToday: dayName === format(startOfToday(), 'eee'),
  }));

  protected nextMonth() {
    this.currentDate.set(addMonths(this.currentDate(), 1));
    this.updateDropdowns();
    this.monthChange.emit(this.currentDate());
    this.date = this.currentDate();
  }

  protected prevMonth() {
    this.currentDate.set(subMonths(this.currentDate(), 1));
    this.updateDropdowns();
    this.monthChange.emit(this.currentDate());
    this.date = this.currentDate();
  }

  protected toCurrentMonth(): void {
    this.currentDate.set(startOfToday());
    this.updateDropdowns();
    this.date = startOfToday();
    this.monthChange.emit(this.currentDate());
  }

  protected onMonthYearChange(): void {
    this.currentDate.set(new Date(this.selectedYear, this.selectedMonth, 1));
    this.monthChange.emit(this.currentDate());
  }

  protected updateDropdowns(): void {
    this.selectedMonth = this.currentDate().getMonth();
    this.selectedYear = this.currentDate().getFullYear();
  }

  protected getMarkerMapKey(date: Date): string {
    return format(date, 'yyyy-MM-dd');
  }

}

export interface CalendarMarkerData<Data = any> {
  date: Date;
  data: Data;
}
